async function requestJson(path, options = {}) {
  const response = await fetch(path, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
    },
    ...options,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.message || `Request failed (${response.status})`);
    error.status = response.status;
    error.code = data.code;
    throw error;
  }
  return data;
}

function buildQuery(params = {}) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === "") continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : "";
}

export async function fetchVehicleModelCopyAdminList(filters = {}) {
  const data = await requestJson(
    `/api/admin/mobility/vehicle-model-copy${buildQuery({
      search: filters.search,
      make_key: filters.makeKey,
      model_key: filters.modelKey,
      locale: filters.locale,
      status: filters.status,
      coverage: filters.coverage,
      link_state: filters.linkState,
      public_source: filters.publicSource,
      limit: filters.limit,
      offset: filters.offset,
    })}`
  );
  return {
    items: data.items || [],
    pagination: data.pagination || { total: 0, limit: 25, offset: 0 },
  };
}

export async function fetchVehicleModelCopyAdminDetail({ vehicleModelId, makeKey, modelKey }) {
  const path = vehicleModelId
    ? `/api/admin/mobility/vehicle-model-copy/models/${encodeURIComponent(vehicleModelId)}`
    : `/api/admin/mobility/vehicle-model-copy/unlinked/detail${buildQuery({
        make_key: makeKey,
        model_key: modelKey,
      })}`;
  const data = await requestJson(path);
  return data.detail;
}

export async function saveVehicleModelCopyDraft(payload) {
  const data = await requestJson("/api/admin/mobility/vehicle-model-copy/draft", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return data.copy;
}

export async function publishVehicleModelCopy(copyId, payload = {}) {
  const data = await requestJson(
    `/api/admin/mobility/vehicle-model-copy/${encodeURIComponent(copyId)}/publish`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
  return data.copy;
}

export async function unpublishVehicleModelCopy(copyId, payload = {}) {
  const data = await requestJson(
    `/api/admin/mobility/vehicle-model-copy/${encodeURIComponent(copyId)}/unpublish`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
  return data.copy;
}

export async function fetchVehicleModelCopyHistory(copyId, pagination = {}) {
  const data = await requestJson(
    `/api/admin/mobility/vehicle-model-copy/${encodeURIComponent(copyId)}/history${buildQuery({
      limit: pagination.limit,
      offset: pagination.offset,
    })}`
  );
  return {
    items: data.items || [],
    pagination: data.pagination || { total: 0, limit: 25, offset: 0 },
  };
}

export async function fetchVehicleModelCopyHistoryVersion(copyId, versionId) {
  const data = await requestJson(
    `/api/admin/mobility/vehicle-model-copy/${encodeURIComponent(copyId)}/history/${encodeURIComponent(versionId)}`
  );
  return data.version;
}

export async function restoreVehicleModelCopyVersion(copyId, versionId, payload = {}) {
  const data = await requestJson(
    `/api/admin/mobility/vehicle-model-copy/${encodeURIComponent(copyId)}/history/${encodeURIComponent(versionId)}/restore`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
  return data.copy;
}

export function buildVehicleModelCopyExportUrl(format = "csv", filters = {}) {
  const path =
    format === "json"
      ? "/api/admin/mobility/vehicle-model-copy/export.json"
      : "/api/admin/mobility/vehicle-model-copy/export.csv";
  return `${path}${buildQuery({
    make_key: filters.makeKey,
    model_key: filters.modelKey,
    locale: filters.locale,
    status: filters.status,
  })}`;
}

export async function validateVehicleModelCopyImport(file) {
  const formData = new FormData();
  formData.append("file", file);
  const response = await fetch("/api/admin/mobility/vehicle-model-copy/import/validate", {
    method: "POST",
    credentials: "include",
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.message || `Request failed (${response.status})`);
    error.status = response.status;
    error.errors = data.errors;
    throw error;
  }
  return data;
}

export async function applyVehicleModelCopyImport({ runId, fileHash }) {
  const data = await requestJson("/api/admin/mobility/vehicle-model-copy/import/apply", {
    method: "POST",
    body: JSON.stringify({ runId, fileHash }),
  });
  return data;
}

export async function fetchVehicleModelCopyImportRun(runId, pagination = {}) {
  const data = await requestJson(
    `/api/admin/mobility/vehicle-model-copy/import-runs/${encodeURIComponent(runId)}${buildQuery({
      outcome: pagination.outcome,
      limit: pagination.limit,
      offset: pagination.offset,
    })}`
  );
  return { run: data.run, rows: data.rows || [] };
}

export async function fetchVehicleModelCopyQualitySummary(filters = {}) {
  const data = await requestJson(
    `/api/admin/mobility/vehicle-model-copy/quality/summary${buildQuery({
      universe: filters.universe,
      make_key: filters.makeKey,
      model_key: filters.modelKey,
      coverage_state: filters.coverageState,
      resolution_method: filters.resolutionMethod,
      issue_type: filters.issueType,
      severity: filters.severity,
      refresh: filters.refresh,
    })}`
  );
  return data;
}

export async function fetchVehicleModelCopyQualityModels(filters = {}) {
  const data = await requestJson(
    `/api/admin/mobility/vehicle-model-copy/quality/models${buildQuery({
      universe: filters.universe,
      make_key: filters.makeKey,
      model_key: filters.modelKey,
      coverage_state: filters.coverageState,
      resolution_method: filters.resolutionMethod,
      min_listing_count: filters.minListingCount,
      unresolved_only: filters.unresolvedOnly,
      limit: filters.limit,
      offset: filters.offset,
      sort: filters.sort,
    })}`
  );
  return { items: data.items || [], pagination: data.pagination || { total: 0 } };
}

export async function fetchVehicleModelCopyQualityIssues(filters = {}) {
  const data = await requestJson(
    `/api/admin/mobility/vehicle-model-copy/quality/issues${buildQuery({
      universe: filters.universe,
      make_key: filters.makeKey,
      model_key: filters.modelKey,
      issue_type: filters.issueType,
      severity: filters.severity,
      locale: filters.locale,
      min_listing_count: filters.minListingCount,
      limit: filters.limit,
      offset: filters.offset,
      sort: filters.sort,
    })}`
  );
  return { items: data.items || [], pagination: data.pagination || { total: 0 } };
}

export async function fetchVehicleModelCopyQualityTrends(filters = {}) {
  const data = await requestJson(
    `/api/admin/mobility/vehicle-model-copy/quality/trends${buildQuery({
      universe: filters.universe,
      limit: filters.limit,
    })}`
  );
  return { trends: data.trends || [] };
}

export async function createVehicleModelCopyQualitySnapshot({ filters = {} } = {}) {
  const data = await requestJson("/api/admin/mobility/vehicle-model-copy/quality/snapshots", {
    method: "POST",
    body: JSON.stringify({ filters }),
  });
  return data;
}

export function buildVehicleModelCopyQualityExportUrl(format = "csv", filters = {}) {
  const path =
    format === "json"
      ? "/api/admin/mobility/vehicle-model-copy/quality/export.json"
      : "/api/admin/mobility/vehicle-model-copy/quality/export.csv";
  return `${path}${buildQuery({
    universe: filters.universe,
    make_key: filters.makeKey,
    model_key: filters.modelKey,
    coverage_state: filters.coverageState,
    issue_type: filters.issueType,
    severity: filters.severity,
    kind: filters.kind || "issues",
  })}`;
}
